// AccountPromoCode.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { resolveFrontendApiBase } from '../utils/apiBase';
import {
  Typography,
  Card,
  Input,
  Button,
  Space,
  Alert,
  message,
  Spin,
  Tag,
  Tooltip
} from 'antd';
import { CheckCircleOutlined, GiftOutlined, QuestionCircleOutlined, TagOutlined } from '@ant-design/icons';
import axios from 'axios';
import { useAccountContext } from '../Account';
import type { TourProps } from 'antd';
import AppTour, { renderAppTourPanel } from '../tours/AppTour';
import {
  ACCOUNT_PROMO_TOUR_EVENT,
  ACCOUNT_PROMO_TOUR_STORAGE_KEY
} from './accountTourEvents';
import './AccountPromoCode.css';

const { Title, Text } = Typography;
const API_BASE = resolveFrontendApiBase();

type PromoPreview = {
  code: string;
  action: string;
  amount: number;
  description?: string;
};

export default function AccountPromoCode(): JSX.Element {
  const { isAuth, usersSharedState, showLoginModal } = useAccountContext();
  const userID: string = usersSharedState?.userID || '';

  const [code, setCode] = useState<string>('');
  const [validating, setValidating] = useState<boolean>(false);
  const [redeeming, setRedeeming] = useState<boolean>(false);
  const [preview, setPreview] = useState<PromoPreview | null>(null);
  const [redeemed, setRedeemed] = useState<{ amount: number; action: string } | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [promoTourOpen, setPromoTourOpen] = useState<boolean>(false);
  const [promoTourStep, setPromoTourStep] = useState<number>(0);
  const [promoTourFlagsChecked, setPromoTourFlagsChecked] = useState<boolean>(false);
  const [promoTourShouldOpen, setPromoTourShouldOpen] = useState<boolean>(false);
  const [promoTourSeen, setPromoTourSeen] = useState<boolean>(false);
  const tourFallbackRef = useRef<HTMLElement | null>(null);

  const normalizedCode = useMemo(() => code.replace(/\s+/g, '').toUpperCase(), [code]);
  const promoDataReady = true;
  const codeHelper = normalizedCode
    ? 'Codes are checked before they are applied to your account.'
    : 'Enter a promo code to check.';

  useEffect(() => {
    setPromoTourFlagsChecked(false);
    setPromoTourSeen(false);
    setPromoTourShouldOpen(false);
    setPromoTourOpen(false);
    setPromoTourStep(0);
  }, [userID]);

  useEffect(() => {
    if (!userID) return;
    if (promoTourFlagsChecked) return;
    let cancelled = false;

    const fetchTourFlags = async () => {
      try {
        const res = await axios.get(`${API_BASE}/user/ui-flags`, {
          params: { userId: userID }
        });
        if (cancelled) return;
        const seen = !!res.data?.uiFlags?.accountPromoTourSeen;
        setPromoTourSeen(seen);
        if (!seen) {
          setPromoTourShouldOpen(true);
        }
      } catch (_err) {
        if (cancelled) return;
        setPromoTourSeen(false);
      } finally {
        if (!cancelled) {
          setPromoTourFlagsChecked(true);
        }
      }
    };

    fetchTourFlags();
    return () => {
      cancelled = true;
    };
  }, [userID, promoTourFlagsChecked]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const pending = window.sessionStorage.getItem(ACCOUNT_PROMO_TOUR_STORAGE_KEY);
    if (pending) {
      window.sessionStorage.removeItem(ACCOUNT_PROMO_TOUR_STORAGE_KEY);
      setPromoTourShouldOpen(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleTourRequest = () => {
      setPromoTourShouldOpen(true);
    };
    window.addEventListener(ACCOUNT_PROMO_TOUR_EVENT, handleTourRequest as EventListener);
    return () => {
      window.removeEventListener(ACCOUNT_PROMO_TOUR_EVENT, handleTourRequest as EventListener);
    };
  }, []);

  useEffect(() => {
    if (!promoTourFlagsChecked) return;
    if (!promoTourShouldOpen) return;
    if (!promoDataReady) return;
    setPromoTourOpen(true);
    setPromoTourStep(0);
    setPromoTourShouldOpen(false);
  }, [promoTourFlagsChecked, promoTourShouldOpen, promoDataReady]);

  const resolveTourTarget = useCallback(
    (element?: HTMLElement | null): HTMLElement => {
      if (element) return element;
      if (typeof document !== 'undefined' && document.body) {
        return document.body;
      }
      if (!tourFallbackRef.current) {
        tourFallbackRef.current =
          typeof document !== 'undefined' ? document.createElement('div') : ({} as HTMLElement);
      }
      return tourFallbackRef.current as HTMLElement;
    },
    []
  );

  const getTourElement = useCallback((id: string) => {
    if (typeof document === 'undefined') return null;
    return document.querySelector(`[data-tour-id="${id}"]`) as HTMLElement | null;
  }, []);

  const renderTeacherPanel = useCallback(
    (content: React.ReactNode) => renderAppTourPanel(content),
    []
  );

  const handlePromoTourChange = useCallback((step: number) => {
    setPromoTourStep(step);
  }, []);

  const handlePromoTourClose = useCallback(async () => {
    setPromoTourOpen(false);
    setPromoTourStep(0);
    setPromoTourShouldOpen(false);
    if (!promoTourSeen) {
      setPromoTourSeen(true);
      if (userID) {
        try {
          await axios.post(`${API_BASE}/user/ui-flags/set`, {
            userId: userID,
            flags: { accountPromoTourSeen: true }
          });
        } catch (_err) {
          // Non-blocking — failure just means we may show the tour again later.
        }
      }
    }
  }, [promoTourSeen, userID]);

  const promoTourSteps = useMemo<TourProps['steps']>(() => {
    const targetOrFallback = (primaryId: string, secondaryId?: string) =>
      resolveTourTarget(
        getTourElement(primaryId) ?? (secondaryId ? getTourElement(secondaryId) : null)
      );

    return [
      {
        title: 'Start with your promo workspace',
        description: renderTeacherPanel(
          <>
            <Text strong>Redeem codes to add Forever Credits.</Text>
            <br />
            <Text type="secondary">
              Validate a code first, then apply it to your account when you’re ready.
            </Text>
          </>
        ),
        target: () => targetOrFallback('promo-main-card')
      },
      {
        title: 'Check a code first',
        description: renderTeacherPanel(
          <>
            <Text strong>Enter your promo code and select “Check code”.</Text>
            <br />
            <Text type="secondary">
              Check validates the code without using it. Redeem applies the one-time credit grant and requires an account.
            </Text>
          </>
        ),
        target: () => targetOrFallback('promo-code-action')
      },
      {
        title: 'Learn how promos work',
        description: renderTeacherPanel(
          <>
            <Text strong>Need the fine print?</Text>
            <br />
            <Text type="secondary">
              Review redemption rules, where to find codes, and that each code is single-use.
            </Text>
          </>
        ),
        target: () => targetOrFallback('promo-info-card', 'promo-main-card')
      }
    ];
  }, [getTourElement, renderTeacherPanel, resolveTourTarget]);

  const handleValidate = async () => {
    setPreview(null);
    setRedeemed(null);
    setValidationError(null);
    setRedeemError(null);
    if (!normalizedCode) {
      setValidationError('Enter a promo code to check.');
      message.warning('Enter a promo code to check');
      return;
    }
    setValidating(true);
    try {
      const res = await axios.post('https://askpoeai-nswdzfky7q-uc.a.run.app/promo/validate', {
        code: normalizedCode
      });
      const data = res.data as { valid: boolean; code: string; action: string; amount: number; description?: string };
      if (data.valid) {
        setPreview({
          code: data.code,
          action: data.action,
          amount: data.amount,
          description: data.description
        });
        message.success('Code confirmed');
      } else {
        const errorText = "We couldn't find that code. Please check the spelling and try again.";
        setValidationError(errorText);
        message.error(errorText);
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to validate promo code';
      setValidationError(detail);
      message.error(detail);
    } finally {
      setValidating(false);
    }
  };

  const handleRedeem = async () => {
    if (!isAuth) {
      showLoginModal();
      return;
    }
    if (!preview) return;

    setRedeemError(null);
    setRedeeming(true);
    try {
      const res = await axios.post('https://askpoeai-nswdzfky7q-uc.a.run.app/promo/redeem', {
        userID,
        code: preview.code
      });
      const data = res.data as { success: boolean; action: string; amount: number };
      if (data.success) {
        setRedeemed({ amount: data.amount, action: data.action });
        setPreview(null);
        setCode('');
        setValidationError(null);
        setRedeemError(null);
        message.success(`Redeemed successfully. Added ${data.amount} Forever Credits.`);
      } else {
        setRedeemError('Failed to redeem promo code');
        message.error('Failed to redeem promo code');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || 'Failed to redeem promo code';
      setRedeemError(detail);
      message.error(detail);
    } finally {
      setRedeeming(false);
    }
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCode(event.target.value.replace(/\s+/g, '').toUpperCase());
    setPreview(null);
    setRedeemed(null);
    setValidationError(null);
    setRedeemError(null);
  };

  const handleRedeemAnother = () => {
    setCode('');
    setPreview(null);
    setRedeemed(null);
    setValidationError(null);
    setRedeemError(null);
  };

  return (
    <div className="account-promo-shell">
      {!isAuth && (
        <Alert
          type="info"
          showIcon
          message="You're not signed in"
          description="You can check a code without signing in. To use it, please sign in."
          className="account-promo-alert"
        />
      )}

      <Card
        bordered
        className="account-promo-card account-promo-card--primary"
        data-tour-id="promo-main-card"
        title={(
          <div className="account-promo-card-title">
            <span className="account-promo-card-icon"><TagOutlined /></span>
            <div className="account-promo-card-copy">
              <span className="account-promo-kicker">Promo Code</span>
              <Title level={3}>Redeem a promo code</Title>
              <Text type="secondary">Check a code, review the benefit, then apply it to your account.</Text>
            </div>
            <Tooltip title="Enter your code to add credits to your account">
              <QuestionCircleOutlined className="account-promo-help-icon" />
            </Tooltip>
          </div>
        )}
      >
        <Spin spinning={validating || redeeming}>
          <div className="account-promo-flow">
            <section className="account-promo-entry" data-tour-id="promo-code-action">
              <div className="account-promo-entry-copy">
                <Text strong>Enter your code</Text>
                <Text type={validationError ? 'danger' : 'secondary'}>{validationError ?? codeHelper}</Text>
              </div>
              <Space.Compact className="account-promo-code-action">
                <Input
                  className="account-promo-input"
                  size="large"
                  placeholder="e.g., WELCOME5000"
                  value={code}
                  onChange={handleCodeChange}
                  onPressEnter={handleValidate}
                  disabled={validating || redeeming}
                />
                <Button
                  type="primary"
                  size="large"
                  onClick={handleValidate}
                  loading={validating}
                  disabled={!normalizedCode || validating || redeeming}
                >
                  Check code
                </Button>
              </Space.Compact>
              <Text type="secondary" className="account-promo-entry-note">
                Each account can redeem a promo code once; codes may also expire.
              </Text>
            </section>

            {preview && (
              <section className="account-promo-preview">
                <div className="account-promo-preview-heading">
                  <span className="account-promo-status-icon account-promo-status-icon--success">
                    <GiftOutlined />
                  </span>
                  <div>
                    <Title level={4}>Code confirmed</Title>
                    <Text type="secondary">Review the benefit before applying it to your account.</Text>
                  </div>
                  <Tag color="green">{preview.code}</Tag>
                </div>

                <div className="account-promo-benefit">
                  <span className="account-promo-benefit-label">Adds</span>
                  <span className="account-promo-benefit-value">
                    {preview.amount.toLocaleString()} {preview.action === 'Forever Credits' ? 'Forever Credits' : preview.action}
                  </span>
                  <Text type="secondary">Applies instantly and can only be redeemed once per account.</Text>
                </div>

                <div className="account-promo-preview-actions">
                  {preview.description && (
                    <Alert
                      type="success"
                      message={preview.description}
                      showIcon
                      className="account-promo-alert"
                    />
                  )}
                  {!isAuth && (
                    <Alert
                      type="info"
                      showIcon
                      message="Sign in to apply this code"
                      description="The code is confirmed. Sign in or create an account to redeem it."
                      className="account-promo-alert"
                    />
                  )}
                  {redeemError && (
                    <Alert
                      type="error"
                      showIcon
                      message={redeemError}
                      className="account-promo-alert"
                    />
                  )}
                  <div className="account-promo-button-row">
                    <Button
                      type="primary"
                      onClick={handleRedeem}
                      loading={redeeming}
                    >
                      Apply to my account
                    </Button>
                    <Button onClick={() => setPreview(null)} disabled={redeeming}>
                      Cancel
                    </Button>
                  </div>
                </div>
              </section>
            )}

            {redeemed && (
              <section className="account-promo-success">
                <span className="account-promo-status-icon account-promo-status-icon--success">
                  <CheckCircleOutlined />
                </span>
                <div>
                  <Title level={4}>Code applied!</Title>
                  <Text>
                    Added {redeemed.amount.toLocaleString()} {redeemed.action === 'Forever Credits' ? 'Forever Credits' : 'benefit'} to your account.
                  </Text>
                </div>
                <Button onClick={handleRedeemAnother}>Redeem another code</Button>
              </section>
            )}
          </div>
        </Spin>
      </Card>

      <Card
        bordered
        className="account-promo-card account-promo-card--info"
        data-tour-id="promo-info-card"
      >
        <div className="account-promo-info-copy">
          <Text strong>How promo codes work</Text>
          <Text type="secondary">
            Promo codes are single-use and tied to your account upon redemption. Promo codes may be available periodically on our Facebook group, Discord, or at conventions we attend.
          </Text>
        </div>
      </Card>

      <AppTour
        open={promoTourOpen}
        steps={promoTourSteps}
        current={promoTourStep}
        onChange={handlePromoTourChange}
        onClose={handlePromoTourClose}
        onFinish={handlePromoTourClose}
      />
    </div>
  );
}
